<template>
  <section class="about-chapter-2">
    <div class="about-chapter-2-content">
      <AboutAnchorNav :section="2" class="px-4"/>
    
      <v-container class="px-4 px-sm-8 py-0 pa-md-0">
        <div class="mb-12 mb-md-16">
          <v-row align="start" justify="space-between" no-gutters>
            <v-col cols="12" md="3">
              <p class="text-section text--fes-magenta mb-4 mb-md-0">
                Shaping the cities of tomorrow
              </p>
            </v-col>

            <v-col cols="12" md="8">
              <p class="text-body text--fes-blue mb-0">
                Asia’s challenges in social-ecological transformation are unique to each city. No part of the city is too small to focus on, even down to sections of a river or a landslide-prone slope on a hill.
              </p>                       
            </v-col>
          </v-row>
        </div>  

        <div
          v-for="n in cities" 
          :key="n.icon + '-cities'"          
          class="about-chapter-2-card hidden-xs-only">
          <div class="about-chapter-2-card-img" :style="{ backgroundImage: 'url(' + require('@/assets/about/chapter4/about-ch4-sectors-' + `${n.icon}` +'.png') + ')' }">
            <p class="text-card white--text text-uppercase">{{ n.label }}</p>
          </div>

          <div class="about-chapter-2-card-desc">
            <p class="text-body text--fes-magenta mb-6">
              Applicable in {{ n.country }}
              <!-- <router-link :to="'/map'" style="text-decoration: none;"><span class="font-weight-bold">{{ n.country }}</span></router-link> -->
            </p>  

            <p class="text-body text--fes-blue mb-4">
              {{ n.desc1 }}
            </p>

            <p class="text-body text--fes-blue mb-4">
              {{ n.desc2 }}
            </p>

            <p v-if="n.desc3" class="text-body text--fes-blue mb-0">
              {{ n.desc3 }}
            </p>            
          </div>
        </div>
      </v-container>

      <div
        v-for="n in cities" 
        :key="n.icon + '-cities-mobile'"          
        class="about-chapter-2-card-mobile hidden-sm-and-up">

        <div 
          class="about-chapter-2-card-img"
          :style="{ backgroundImage: 'url(' + require('@/assets/about/chapter4/about-ch4-sectors-' + `${n.icon}` +'.png') + ')' }">
          <p class="text-card-title-2 white--text text-uppercase">{{ n.label }}</p>
        </div>

        <div class="about-chapter-2-card-desc pa-6">
          <p class="text-body text--fes-magenta mb-6">
            Applicable in {{ n.country }}
            <!-- <span class="font-weight-bold">{{ n.country }}</span> -->
          </p>               

          <p class="text-body text--fes-blue mb-4">
            {{ n.desc1 }}
          </p>

          <p class="text-body text--fes-blue mb-4">
            {{ n.desc2 }}
          </p>

          <p v-if="n.desc3" class="text-body text--fes-blue mb-0">
            {{ n.desc3 }}
          </p>              
        </div>
      </div>       
    </div>      
  </section>  
</template>

<script>
import AboutAnchorNav from '@/components/layout/AboutAnchorNav'

export default {
  name: 'AboutChapter4',
  components: {
    AboutAnchorNav
  },  
  data() {
    return {
      cities: [
        {
          "icon":"urban-mobility",
          "label":"Mobility",
          "country":"Jakarta",
          "desc1":"As people move to cities or commute in daily, roads get ever busier. In Jakarta, around 75 percent of daily trips happen on private vehicles. This causes congestion, air pollution, accidents and more greenhouse gas emissions.",
          "desc2":"Cities must provide reliable and comfortable transportation infrastructure, especially around active modes of transit such as cycling. Blocked or damaged sidewalks, for example, require maintenance so people with disabilities, the elderly or children can use them."
        },
        {
          "icon":"housing",
          "label":"Housing",
          "country":"Cebu City",
          "desc1":"Rapid urbanisation and overcrowding has left millions without access to decent housing. All over Asia, much public housing is cramped, lacking in amenities, poorly built and sited in disaster-prone areas.",
          "desc2":"Relocating settlements to safer sites is a key first step. Cities must ensure access to basic amenities and open spaces, involve diverse groups in housing policymaking, and integrate social and environmental concerns into projects."
        },
        {
          "icon":"urban-water",
          "label":"Water",
          "country":"Mumbai",
          "desc1":"As climate change raises sea-levels and intensifies rainfall, the clogged drainage systems of many Asian cities are overflowing. Unplanned land reclamation, illegal settlement and unsafe building construction place many housing areas at risk.",
          "desc2":"Authorities should consider natural systems and hotspots in their solutions and resist short-term fixes. Improving drainage systems, enforcing development restrictions in flood zones and involving locals through cultural frameworks are solid strategies."
        },
        {
          "icon":"energy",
          "label":"Energy",
          "country":"Hanoi",
          "desc1":"Fast-growing cities in Southeast Asia will consume most of the world’s new energy demand in the coming decades. Energy security is a priority, but the region’s growth policies still rely on fossil-fuel-intensive pathways, and investors here are reluctant to embrace climate concerns in their decisions.",
          "desc2":"The region must develop its considerable renewable resources, such as solar and wind, to hit important emissions targets. It needs to encourage investment and involve citizens in a just transition, thereby creating fair and decent jobs and avoiding stranded assets."
        },
        {
          "icon":"circular-economy",
          "label":"Circular economy",
          "country":"New Delhi",
          "desc1":"As consumerism grows with urbanisation and economic growth, more waste is produced each day. Mixing waste makes it hard to decompose organic matter or recycle recyclables. This fills up landfills and creates health hazards such as methane emissions, soil and groundwater seepage, and solid waste contamination.",
          "desc2":"Cities can improve waste management systems, along with encouraging circular economy practices—such as repairing and reusing materials—across sectors and industries."
        },
        {
          "icon":"social-participation",
          "label":"Social participation",
          "country":"Iloilo City",
          "desc1":"Social participation ensures that minority and disadvantaged voices are heard. Women, for example, face gender discrimination in many forms, from exclusion from services, politics and the economy to physical violence. For this reason, women experience and use the city very differently from men, and their needs must be made central in urban policy.",
          "desc2":"Yet political resistance, unclear government communication, and limited capacity can make it challenging for the disadvantaged to gain a voice in the city. Citizens often have no time, resources, or interest in participatory governance, leaving powerful groups overrepresented in policy.",
          "desc3":"Encouraging network building and alliances between people, civic organisations, and the government can help disadvantaged groups be heard. Forums for knowledge transfer within and outside communities protect local experiences and expertise, turning it into sensitive policy."
        }
      ]
    };
  },
}
</script>

<style lang="scss" scoped>
.about-chapter-2 {
  scroll-margin-top: 70px;
  
  .about-chapter-2-content {
    padding: 96px 0;
    background: linear-gradient(180deg, #EAF4E4 0%, #AFCACE 100%);

    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      padding: 48px 0px;
    }

    .about-chapter-2-card {
      margin-bottom: 32px;
      display: flex;

      .about-chapter-2-card-img {
        min-height: 360px;
        width: calc(100% * 0.333 - 32px);
        height: 100%;
        padding: 24px;
        background-position: center;
        background-size: cover;

        @media #{map-get($display-breakpoints, 'sm-and-down')} {
          min-height: 335px;     
          width: calc(100% * 0.4 - 32px);             
        }      
      }

      .about-chapter-2-card-desc {
        background: rgba(250, 250, 250, 0.72); 
        width: calc(100% * 0.666 + 32px);        
        backdrop-filter: blur(10px);
        padding: 32px;
        @media #{map-get($display-breakpoints, 'sm-and-down')} {
          width: calc(100% * 0.6 + 32px);        
        }        

        p:last-child {
          margin-bottom: 0px !important;
        }        
      }
    }
    

    div.about-chapter-2-card:nth-of-type(7) {
      .about-chapter-2-card-img {
        min-height: 420px;

        @media #{map-get($display-breakpoints, 'md-and-down')} {
          min-height: 500px;       
        }          
      }      
    }
    
    .about-chapter-2-card-mobile {
      margin-bottom: 32px;
      .about-chapter-2-card-img {
        min-height: 250px;
        width: 100%;
        height: 100%;
        padding: 24px;
        background-position: center;
      }

      .about-chapter-2-card-desc {
        background: rgba(250, 250, 250, 0.72); 
        backdrop-filter: blur(10px);

        p:last-child {
          margin-bottom: 0px;
        }
      }   
    } 
    .about-chapter-2-card-mobile:last-child {
      margin-bottom: 0;
    } 
  }
}

.about-chapter-2-hero {
  // background: url("~@/assets/about-chapter-2-hero.png") 
  background: url("https://picsum.photos/1440/576");   
  padding: 144px 0;   
  background-size: cover
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .about-chapter-2-content {
    padding: 48px 4px;
  }  

  .about-chapter-2-hero {
    margin-bottom: 48px 4px;   
  }
}
</style>